import { css } from '@emotion/react'
import {
  DesignToken,
  SpacingToken,
  Text,
} from '@livechat/design-system-react-components'

import type { IApplication } from '@/apis/apps/interfaces'
import PriceFormatter from '@/components/Checkout/PriceFormatter'
import useParamSelector from '@/hooks/useParamSelector'
import mediaQueries from '@/layout/media-queries'
import { useTypedSelector } from '@/store/hooks'
import { type IPaymentCharge } from '@/store/payment/interfaces'
import {
  getCartAgentsCountForProduct,
  getChargeType,
  getPaymentCharge,
} from '@/store/selectors'
import { calculateChargePrice } from '@/utils/products'
import { withVar } from '@/utils/styles'

const wrapperCss = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  gap: var(${SpacingToken.Spacing05});

  ${mediaQueries.xsmallScreen()} {
    align-items: flex-start;
  }

  ${mediaQueries.smallScreen()} {
    margin-top: var(${SpacingToken.Spacing1});
  }
`

interface Props {
  item: IApplication
}

const PriceSummary = ({ item }: Props) => {
  const charge: IPaymentCharge = useTypedSelector(getPaymentCharge)
  const chargeType = useTypedSelector(getChargeType)
  const agentsCount = useParamSelector(
    getCartAgentsCountForProduct,
    item?.product
  )

  if (!item.payment) {
    return (
      <div css={wrapperCss}>
        <Text size="md" bold>
          Free to install
        </Text>
      </div>
    )
  }

  const isPerAccount = charge?.per_account || item?.payment?.perAccount
  const months = charge?.months || 1
  const frequency = chargeType || item?.payment?.frequency

  const { baseTotalPrice, baseUnitPrice } = calculateChargePrice({
    charge,
    agentsCount,
    payment: item?.payment,
  })

  return (
    <div css={wrapperCss}>
      <Text size="md" bold>
        <PriceFormatter
          value={baseTotalPrice}
          frequency={frequency}
          months={months}
        />
      </Text>

      {isPerAccount && agentsCount > 0 && (
        <Text
          size="xs"
          customColor={withVar(DesignToken.ContentBasicSecondary)}
        >
          <PriceFormatter value={baseUnitPrice} /> ✕ {agentsCount} Agent
          {agentsCount > 1 ? 's' : ''}
        </Text>
      )}

      {!isPerAccount && (
        <Text
          size="xs"
          customColor={withVar(DesignToken.ContentBasicSecondary)}
        >
          Per organization
        </Text>
      )}
    </div>
  )
}

export default PriceSummary
